/**
 * Safety numbers — out-of-band identity verification, Signal-style. Two
 * people compare a 60-digit number (or its QR); if it matches, they guarantee
 * that there is no man-in-the-middle (MITM) impersonating the public identity
 * keys.
 *
 * fingerprint(id, IK) = iterate SHA-512 over (version || IK_dh || IK_sig || id).
 * The combined number sorts the two fingerprints (independent of who is looking) and
 * concatenates 30 digits from each → 60 deterministic and symmetric digits.
 */

import { concatBytes, utf8Encode } from "./crypto/encoding";

const VERSION = new Uint8Array([0x00, 0x00]);
const DEFAULT_ITERATIONS = 1024;

function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

async function iteratedHash(
  seed: Uint8Array,
  identityKeys: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  let hash = concatBytes(seed, identityKeys);
  for (let i = 0; i < iterations; i++) {
    hash = new Uint8Array(
      await crypto.subtle.digest("SHA-512", ab(concatBytes(hash, identityKeys))),
    );
  }
  return hash;
}

/** 30 digits (6 groups of 5) derived from one party's fingerprint. */
function displayDigits(fingerprint: Uint8Array): string {
  let out = "";
  for (let group = 0; group < 6; group++) {
    const chunk = fingerprint.subarray(group * 5, group * 5 + 5);
    let value = 0;
    for (const b of chunk) {
      value = (value * 256 + b) % 100_000;
    }
    out += value.toString().padStart(5, "0");
  }
  return out;
}

export interface SafetyIdentity {
  readonly stableId: string;
  readonly identityDhPub: Uint8Array;
  readonly identitySigPub: Uint8Array;
}

async function fingerprintOf(who: SafetyIdentity, iterations: number): Promise<string> {
  const keys = concatBytes(who.identityDhPub, who.identitySigPub);
  const seed = concatBytes(VERSION, utf8Encode(who.stableId));
  return displayDigits(await iteratedHash(seed, keys, iterations));
}

/**
 * Combined safety number (60 digits). Symmetric: `A,B` == `B,A`.
 * `stableId` must be a stable identifier of the party (e.g. its encoded
 * identity key, or the account id if any); it must not change between views.
 */
export async function safetyNumber(
  self: SafetyIdentity,
  peer: SafetyIdentity,
  iterations = DEFAULT_ITERATIONS,
): Promise<string> {
  const [a, b] = await Promise.all([
    fingerprintOf(self, iterations),
    fingerprintOf(peer, iterations),
  ]);
  const [first, second] = a < b ? [a, b] : [b, a];
  return first + second;
}

/** Readable format: 12 groups of 5 digits separated by spaces. */
export function formatSafetyNumber(number: string): string {
  return (number.match(/.{1,5}/g) ?? []).join(" ");
}
